import React, { useState, useEffect } from 'react';
import { HelpCircle, Plus, Clock, CheckCircle, MessageSquare, Loader2, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { normalizeAppError } from '../../utils/apiErrors';
import { API } from '../../config/apiBase';


const FAQ = [
    { q: 'Como alterar minha foto de perfil?', a: 'Va em Meu Perfil e clique no icone de camera sobre a foto.' },
    { q: 'Esqueci de bater ponto, o que fazer?', a: 'Va em Solicitações e abra um pedido de ajuste de ponto com o motivo.' },
    { q: 'Como funciona o geofence?', a: 'O geofence valida se você esta dentro da area permitida ao bater ponto.' },
    { q: 'Meu ponto foi negado, por que?', a: 'Pode ser por localização fora da area, GPS impreciso ou sem permissao de localização.' },
];

export default function SupportPage() {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ titulo: '', descrição: '', categoria: 'geral', prioridade: 'media' });
    const [saving, setSaving] = useState(false);
    const [faqOpen, setFaqOpen] = useState(null);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => {
        fetch(`${API}/suporte/tickets`, { headers }).then(r => r.json())
            .then(data => { setTickets(Array.isArray(data) ? data : []); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const r = await fetch(`${API}/suporte/tickets`, { method: 'POST', headers, body: JSON.stringify(form) });
            const data = await r.json();
            if (!r.ok) throw new Error(data.detail || 'Erro');
            toast.success('Chamado aberto!');
            setTickets(prev => [data, ...prev]);
            setShowForm(false);
            setForm({ titulo: '', descrição: '', categoria: 'geral', prioridade: 'media' });
        } catch (err) { toast.error(normalizeAppError(err).message); }
        setSaving(false);
    };

    const statusConfig = { aberto: { color: '#F59E0B', label: 'Aberto' }, em_andamento: { color: '#3B82F6', label: 'Em andamento' }, resolvido: { color: '#10B981', label: 'Resolvido' }, fechado: { color: '#6B7280', label: 'Fechado' } };

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div className="space-y-5" data-testid="suporte-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <p className="text-sm" style={{ color: 'var(--muted)' }}>{tickets.length} chamado(s)</p>
                <Button onClick={() => setShowForm(!showForm)} className="btn-gold text-sm" data-testid="btn-novo-chamado">
                    <Plus size={14} className="mr-1" /> Novo Chamado
                </Button>
            </div>

            {/* Form */}
            {showForm && (
                <form onSubmit={handleCreate} className="rounded-xl p-4 sm:p-5 space-y-3" style={{ background: 'var(--surface)', border: '1px solid rgba(var(--sv-accent-rgb), 0.3)' }} data-testid="form-chamado">
                    <h4 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Novo Chamado</h4>
                    <input placeholder="Titulo" value={form.titulo} onChange={e => setForm({...form, titulo: e.target.value})} required
                        className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                    <div className="grid grid-cols-2 gap-2">
                        <select value={form.categoria} onChange={e => setForm({...form, categoria: e.target.value})}
                            className="px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                            <option value="geral">Geral</option>
                            <option value="ponto">Ponto Digital</option>
                            <option value="conta">Conta</option>
                            <option value="tecnico">Técnico</option>
                            <option value="financeiro">Financeiro</option>
                        </select>
                        <select value={form.prioridade} onChange={e => setForm({...form, prioridade: e.target.value})}
                            className="px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                            <option value="baixa">Baixa</option>
                            <option value="media">Média</option>
                            <option value="alta">Alta</option>
                        </select>
                    </div>
                    <textarea placeholder="Descreva o problema..." value={form.descricao} onChange={e => setForm({...form, descricao: e.target.value})} required rows={3}
                        className="w-full px-3 py-2.5 rounded-lg text-sm resize-none" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                    <div className="flex gap-2">
                        <Button type="submit" disabled={saving} className="btn-gold text-sm">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <><Send size={12} className="mr-1" /> Enviar</>}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="text-sm">Cancelar</Button>
                    </div>
                </form>
            )}

            {/* Tickets */}
            {tickets.length === 0 ? (
                <div className="text-center py-12 rounded-xl" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <MessageSquare size={36} className="mx-auto mb-3" style={{ color: 'var(--muted)' }} />
                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Nenhum chamado</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Abra um chamado para obter ajuda</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {tickets.map((t) => {
                        const sc = statusConfig[t.status] || statusConfig.aberto;
                        return (
                            <div key={t.ticket_id} className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`ticket-${t.ticket_id}`}>
                                <div className="flex items-start justify-between mb-1">
                                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{t.titulo}</p>
                                    <span className="text-[10px] px-2 py-0.5 rounded-full shrink-0" style={{ background: `${sc.color}12`, color: sc.color }}>{sc.label}</span>
                                </div>
                                <p className="text-xs" style={{ color: 'var(--muted)' }}>{t.descricao?.slice(0, 80)}{t.descricao?.length > 80 ? '...' : ''}</p>
                                <div className="flex items-center gap-3 mt-2 text-[10px]" style={{ color: 'var(--muted)' }}>
                                    <span>{t.categoria}</span>
                                    <span>{t.prioridade}</span>
                                    <span>{t.created_at ? new Date(t.created_at).toLocaleDateString('pt-BR') : ''}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* FAQ */}
            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Perguntas Frequentes</h3>
                <div className="space-y-1.5">
                    {FAQ.map((f, i) => (
                        <div key={i} className="rounded-lg overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                            <button onClick={() => setFaqOpen(faqOpen === i ? null : i)} className="w-full flex items-center justify-between p-3 text-left">
                                <span className="text-xs font-medium" style={{ color: 'var(--text)' }}>{f.q}</span>
                                {faqOpen === i ? <ChevronUp size={14} style={{ color: 'var(--muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--muted)' }} />}
                            </button>
                            {faqOpen === i && <p className="px-3 pb-3 text-xs" style={{ color: 'var(--muted)' }}>{f.a}</p>}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
